import styled from '@emotion/styled';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import videojs, { VideoJsPlayer } from 'video.js';
import { VideoTrack } from '../../../Player/PlayerModels';
import playerOptions from '../../../Player/PlayerConfigs';
import { HlsPlayerProps, PlayerChromeApi } from './VideoPlayerTypes';
import { PreviewContainer } from './UI';

const PreviewShell = styled(PreviewContainer)({
  borderRadius: 'inherit',
  overflow: 'hidden',
});

function toVideoTrack(source: VideoTrack | string): VideoTrack {
  if (typeof source === 'string') {
    return { id: undefined, src: source, type: 'application/x-mpegURL' };
  }
  return source;
}

function stopChromeEvent(e?: MouseEvent<HTMLElement>) {
  if (!e) return;
  e.preventDefault();
  e.stopPropagation();
  if (e.currentTarget instanceof HTMLElement) e.currentTarget.blur();
}

const ContentChromeRoot = styled.div({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  zIndex: 2,
  overflow: 'hidden',
});

export default function HlsPlayer({
  source,
  poster,
  active = true,
  loop = false,
  autoplay = false,
  live = false,
  playLimit,
  onPlay,
  onPause,
  onEnded,
  videoVisible = true,
  onRequestVideoVisible,
  previewChrome = true,
  children,
}: HlsPlayerProps) {
  const hasChrome = typeof children === 'function';
  const usePreviewWrapper = hasChrome && previewChrome;

  const containerRef = useRef<HTMLDivElement>(null);
  const videoHostRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<VideoJsPlayer | null>(null);
  const playAfterVisibleRef = useRef(false);
  const wasPlayingRef = useRef(false);
  const fullscreenRequestedRef = useRef(false);
  const limitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onFs = () => {
      const el = document.fullscreenElement;
      const ours = !!(el && containerRef.current && el === containerRef.current);
      setIsFullscreen(ours);
      if (el) return;
      if (!fullscreenRequestedRef.current) return;
      fullscreenRequestedRef.current = false;
    };
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  useEffect(() => {
    if (!active) return;

    const track = toVideoTrack(source);
    if (!track.src) return;

    const videoElement = document.createElement('video');
    videoElement.setAttribute('playsinline', 'playsinline');
    videoHostRef.current?.appendChild(videoElement);

    const player = videojs(videoElement, {
      ...playerOptions,
      autoplay: false,
      muted: true,
      controls: false,
      loop,
      sources: [track],
      poster,
    });

    playerRef.current = player;

    const clearLimit = () => {
      if (limitTimerRef.current) {
        clearTimeout(limitTimerRef.current);
        limitTimerRef.current = null;
      }
    };

    const onPlayHandler = () => {
      setPlaying(true);
      onPlay?.();
      if (playLimit && !limitTimerRef.current) {
        limitTimerRef.current = setTimeout(() => {
          limitTimerRef.current = null;
          if (!player.isDisposed()) player.pause();
          onEnded?.();
        }, playLimit * 1000);
      }
    };
    const onPauseHandler = () => {
      clearLimit();
      setPlaying(false);
      onPause?.();
    };
    const onEndedHandler = () => {
      clearLimit();
      if (!player.isDisposed()) {
        player.muted(true);
      }
      setMuted(true);
      setPlaying(false);
      onEnded?.();
    };

    player.on('play', onPlayHandler);
    player.on('pause', onPauseHandler);
    player.on('ended', onEndedHandler);

    player.ready(() => {
      if (player.isDisposed()) return;
      setPlaying(!player.paused());
      setMuted(player.muted());
      if (autoplay && videoVisible) {
        if (!live) player.currentTime(0);
        player.play()?.catch((e: Error & { name?: string }) => {
          if (e?.name === 'AbortError') return;
        });
      }
    });

    return () => {
      clearLimit();
      player.off('play', onPlayHandler);
      player.off('pause', onPauseHandler);
      player.off('ended', onEndedHandler);
      if (!player.isDisposed()) {
        // Tear the HLS source down before disposing. video.js `dispose()` alone leaves the
        // VHS MediaSource / segment loaders lingering across rapid mount-dispose cycles
        // (e.g. hovering live cards repeatedly); the browser then stops opening new
        // MediaSources, so the playlist loads but .ts segments never fetch and playback stalls.
        try {
          const mediaEl = videoHostRef.current?.querySelector('video') as HTMLVideoElement | null;
          player.pause();
          (player as VideoJsPlayer & { reset?: () => void }).reset?.();
          if (mediaEl) {
            mediaEl.removeAttribute('src');
            mediaEl.load();
          }
        } catch {
          // ignore teardown errors — we still dispose below
        }
        player.dispose();
      }
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, source, poster, loop, autoplay, live, playLimit]);

  useEffect(() => {
    const p = playerRef.current;
    if (!p || p.isDisposed()) return;
    if (!videoVisible) {
      // Remember if we were playing so we can resume when it becomes visible again
      // (e.g. scrolled back into view); a user-paused video stays paused.
      wasPlayingRef.current = !p.paused();
      p.pause();
      return;
    }
    if (playAfterVisibleRef.current || wasPlayingRef.current) {
      playAfterVisibleRef.current = false;
      wasPlayingRef.current = false;
      p.play();
    }
  }, [videoVisible]);

  const toggleMute = useCallback(() => {
    const p = playerRef.current;
    if (!p || p.isDisposed()) return;
    const m = !p.muted();
    p.muted(m);
    setMuted(m);
  }, []);

  const togglePlay = useCallback(async () => {
    if (!videoVisible) {
      playAfterVisibleRef.current = true;
      onRequestVideoVisible?.();
      return;
    }
    const p = playerRef.current;
    if (!p || p.isDisposed()) return;
    if (p.paused()) {
      try {
        await p.play();
      } catch {
        // autoplay restrictions
      }
    } else {
      p.pause();
    }
  }, [videoVisible, onRequestVideoVisible]);

  const toggleFullscreen = useCallback((e?: MouseEvent<HTMLElement>) => {
    stopChromeEvent(e);
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      fullscreenRequestedRef.current = true;
      container.requestFullscreen?.().catch(() => {
        fullscreenRequestedRef.current = false;
      });
    }
  }, []);

  if (!active) return null;

  const api: PlayerChromeApi = {
    muted,
    playing,
    isFullscreen,
    toggleMute,
    togglePlay,
    toggleFullscreen,
  };

  const videoHost = (
    <VideoHost
      ref={videoHostRef}
      data-hls-preview-video={hasChrome ? 'true' : undefined}
      data-active={hasChrome ? (active ? 'true' : 'false') : undefined}
      $chrome={hasChrome}
      $hidden={hasChrome && !videoVisible}
    />
  );

  if (!hasChrome) {
    return (
      <VideoHost ref={videoHostRef} $chrome={false} />
    );
  }

  const Wrapper = usePreviewWrapper ? PreviewShell : ContentChromeRoot;

  return (
    <Wrapper
      ref={containerRef}
      data-active={active ? 'true' : 'false'}
    >
      {videoHost}
      {children(api)}
    </Wrapper>
  );
}

const VideoHost = styled.div<{ $chrome?: boolean; $hidden?: boolean }>(({ $chrome, $hidden }) => ({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  zIndex: 2,
  overflow: 'hidden',
  ...($chrome && {
    borderRadius: 'inherit',
    transition: 'opacity 240ms ease',
    pointerEvents: 'none',
    '&[data-active="true"]': {
      opacity: 1,
    },
  }),
  ...($hidden && {
    opacity: 0,
    pointerEvents: 'none',
  }),

  '& .video-js, & video': {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center top',
    ...($chrome && {
      borderRadius: 'inherit',
      overflow: 'hidden',
      zIndex: 3,
    }),
  },

  ...($chrome && {
    '& .vjs-poster': {
      display: 'block',
      verticalAlign: 'middle',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center top',
      backgroundSize: 'cover',
      backgroundColor: '#000',
      cursor: 'pointer',
      margin: 0,
      padding: 0,
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      borderRadius: 'inherit',
      zIndex: $hidden ? 5 : 2,
    },
  }),

  '& .vjs-loading-spinner': {
    display: 'none !important',
  },

  '& video, & .vjs-tech': {
    display: 'block',
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center top',
  },
}));
